import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { Box } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { Card, Chip, Text } from "react-native-paper";
import type { EXAM_STATUS } from "types/tag";
import { buttonConfig } from "../constants";
import { getExamStatusIcon } from "../helpers/practice-tests-data";
import { getProethicaModuleInteractionState } from "../helpers/proethica-module-list";
import { ModuleThumbnail } from "./ModuleThumbnail";

type Props = {
	title: string;
	subtitle: string;
	statusLabel: string;
	status: EXAM_STATUS;
	cta: string;
	thumbnailUrl: string;
	hasAccess?: boolean;
	disabled?: boolean;
	onPress: () => void;
	loading?: boolean;
	numItems?: number;
	duration?: number;
};

export const ProethicaModuleListItem = ({
	title,
	subtitle,
	statusLabel,
	status,
	cta,
	thumbnailUrl,
	hasAccess = true,
	disabled,
	onPress,
	loading,
	numItems,
	duration,
}: Props) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	const { buttonMode, buttonIcon } = buttonConfig[status];
	const examStatusIcon = getExamStatusIcon(status);
	const { needsPurchase, isCurriculumLocked } =
		getProethicaModuleInteractionState(
			hasAccess,
			disabled,
			status === "COMPLETE",
		);

	return (
		<Card
			mode="outlined"
			onPress={isCurriculumLocked ? undefined : onPress}
			accessibilityRole="button"
			accessibilityLabel={`${title}. ${statusLabel}. ${cta}`}
			style={{
				borderRadius: 12,
				overflow: "hidden",
				backgroundColor: theme.colors.surface,
				borderColor: theme.colors.outlineVariant,
				marginHorizontal: isMobile ? 16 : 0,
				marginVertical: 6,
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				padding={12}
				style={{ gap: 12 }}
			>
				<ModuleThumbnail
					size={72}
					title={title}
					thumbnailUrl={thumbnailUrl}
					locked={isCurriculumLocked}
				/>

				<Box flex={1} style={{ gap: 4 }}>
					<Text
						variant="titleSmall"
						style={{
							fontWeight: "bold",
							color: theme.colors.onSurface,
						}}
						numberOfLines={1}
					>
						{title}
					</Text>
					<Text
						variant="bodySmall"
						style={{
							color: theme.colors.onSurfaceVariant,
						}}
						numberOfLines={2}
					>
						{subtitle}
					</Text>
					<Box
						flexDirection="row"
						flexWrap="wrap"
						alignItems="center"
						style={{ gap: 6, marginTop: 8 }}
					>
						{numItems !== undefined && (
							<Chip
								compact
								textStyle={{
									fontSize: 11,
									fontWeight: "600",
									color: theme.colors.onSurfaceVariant,
								}}
								style={{
									height: 24,
									justifyContent: "center",
									backgroundColor: theme.colors.surfaceVariant,
									borderRadius: 6,
								}}
							>
								{numItems} items
							</Chip>
						)}
						{duration !== undefined && (
							<Chip
								compact
								textStyle={{
									fontSize: 11,
									fontWeight: "600",
									color: theme.colors.primary,
								}}
								style={{
									height: 24,
									justifyContent: "center",
									backgroundColor: theme.colors.primaryContainer,
									borderRadius: 6,
								}}
							>
								{duration} min
							</Chip>
						)}
						<Chip
							compact
							icon={examStatusIcon}
							textStyle={{ fontSize: 11, fontWeight: "500" }}
							style={{
								height: 24,
								justifyContent: "center",
								borderRadius: 6,
							}}
						>
							{statusLabel}
						</Chip>
					</Box>
				</Box>

				<Box justifyContent="center" paddingLeft={4}>
					<ModuleActionButton
						mode={needsPurchase ? "contained-tonal" : buttonMode}
						icon={needsPurchase ? "lock-outline" : buttonIcon}
						cta={cta}
						loading={loading}
						disabled={needsPurchase ? false : loading || isCurriculumLocked}
						compact={isMobile}
						onPress={onPress}
					/>
				</Box>
			</Box>
		</Card>
	);
};
